import React, { useState, useEffect, useRef } from "react";
import "./ScenarioBrowser.css";
import { apiGet } from "../../services/api";
import { Scenario, PersonaType } from "../../types";

interface ScenarioBrowserProps {
  onStartSession: (scenarioId: string, personaType: PersonaType, mode: "text" | "voice", voiceId?: string) => void;
  preselectedScenarioId?: string;
}

export function ScenarioBrowser({ onStartSession, preselectedScenarioId }: ScenarioBrowserProps) {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [selectedPersona, setSelectedPersona] = useState<PersonaType>("skeptical");
  const [selectedVoice, setSelectedVoice] = useState("matthew");
  const [filterType, setFilterType] = useState("");
  const selectedCardRef = useRef<HTMLButtonElement>(null);
  const startPanelRef = useRef<HTMLDivElement>(null);

  const VOICES = [
    { id: "matthew", label: "Matthew", lang: "English (US)", gender: "Masculine" },
    { id: "tiffany", label: "Tiffany", lang: "English (US)", gender: "Feminine" },
    { id: "amy", label: "Amy", lang: "English (UK)", gender: "Feminine" },
    { id: "olivia", label: "Olivia", lang: "English (AU)", gender: "Feminine" },
    { id: "lupe", label: "Lupe", lang: "Spanish (US)", gender: "Feminine" },
    { id: "carlos", label: "Carlos", lang: "Spanish", gender: "Masculine" },
    { id: "ambre", label: "Ambre", lang: "French", gender: "Feminine" },
    { id: "florian", label: "Florian", lang: "French", gender: "Masculine" },
    { id: "beatrice", label: "Beatrice", lang: "Italian", gender: "Feminine" },
    { id: "lorenzo", label: "Lorenzo", lang: "Italian", gender: "Masculine" },
    { id: "greta", label: "Greta", lang: "German", gender: "Feminine" },
    { id: "lennart", label: "Lennart", lang: "German", gender: "Masculine" },
  ];

  useEffect(() => {
    loadScenarios();
  }, []);

  // Auto-select preselected scenario after load
  useEffect(() => {
    if (preselectedScenarioId && scenarios.length > 0 && !selectedScenario) {
      const match = scenarios.find((s) => s.scenarioId === preselectedScenarioId);
      if (match) {
        setSelectedScenario(match);
        // Clear filter so the card is visible
        setFilterType("");
      }
    }
  }, [preselectedScenarioId, scenarios]);

  // Scroll to selected card (from preselected scenario link)
  useEffect(() => {
    if (selectedCardRef.current && preselectedScenarioId) {
      selectedCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedScenario, preselectedScenarioId]);

  // Scroll to start panel when a scenario is selected
  useEffect(() => {
    if (selectedScenario && startPanelRef.current) {
      setTimeout(() => startPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100);
    }
  }, [selectedScenario]);

  const loadScenarios = async () => {
    try {
      const result = await apiGet<{ items: Scenario[] }>("/api/scenarios");
      setScenarios(result.items.filter((s) => s.isActive));
    } catch (err) {
      console.error("Failed to load scenarios:", err);
      setError("Failed to load scenarios");
    } finally {
      setLoading(false);
    }
  };

  const filtered = filterType ? scenarios.filter((s) => s.type === filterType) : scenarios;

  const difficultyColor = (d: string) =>
    d === "beginner" ? "var(--color-success)" : d === "intermediate" ? "var(--color-warning)" : "var(--color-error)";

  if (loading) return <div className="protrain-scenario-loading">Loading scenarios...</div>;
  if (error) return <div className="protrain-scenario-error">{error}</div>;

  return (
    <div className="protrain-scenario-browser">
      <div className="protrain-scenario-filters">
        {["", "cold_call", "discovery", "objection_handling"].map((type) => (
          <button
            key={type}
            className={`protrain-scenario-filter-btn ${filterType === type ? "protrain-scenario-filter-btn--active" : ""}`}
            onClick={() => setFilterType(type)}
          >
            {type ? type.replace("_", " ") : "All"}
          </button>
        ))}
      </div>

      <div className="protrain-scenario-grid">
        {filtered.map((scenario) => (
          <button
            key={scenario.scenarioId}
            ref={selectedScenario?.scenarioId === scenario.scenarioId ? selectedCardRef : undefined}
            className={`protrain-scenario-card ${selectedScenario?.scenarioId === scenario.scenarioId ? "protrain-scenario-card--selected" : ""}`}
            onClick={() => setSelectedScenario(scenario)}
          >
            <div className="protrain-scenario-card-header">
              <span className="protrain-scenario-card-type">{scenario.type.replace("_", " ")}</span>
              <span className="protrain-scenario-card-difficulty" style={{ color: difficultyColor(scenario.difficulty) }}>
                {scenario.difficulty}
              </span>
            </div>
            <h3 className="protrain-scenario-card-name">{scenario.name}</h3>
            <p className="protrain-scenario-card-desc">{scenario.description}</p>
            {scenario.goal && (
              <p className="protrain-scenario-card-goal">🎯 {scenario.goal}</p>
            )}
            <div className="protrain-scenario-card-personas">
              {scenario.personas.map((p) => (
                <span key={p} className="protrain-scenario-card-persona-badge">{p}</span>
              ))}
            </div>
          </button>
        ))}
      </div>

      {selectedScenario && (
        <div className="protrain-scenario-start-panel" ref={startPanelRef}>
          <h3>Start: {selectedScenario.name}</h3>
          <div className="protrain-scenario-persona-select">
            <label>Persona:</label>
            <div className="protrain-scenario-persona-options">
              {selectedScenario.personas.map((p) => (
                <button
                  key={p}
                  className={`protrain-scenario-persona-btn ${selectedPersona === p ? "protrain-scenario-persona-btn--active" : ""}`}
                  onClick={() => setSelectedPersona(p)}
                >
                  {p === "skeptical" ? "🤨" : p === "technical" ? "🔧" : "👔"} {p}
                </button>
              ))}
            </div>
          </div>
          <div className="protrain-scenario-mode-buttons">
            <button
              className="protrain-scenario-start-btn protrain-scenario-start-btn--text"
              onClick={() => onStartSession(selectedScenario.scenarioId, selectedPersona, "text")}
            >
              💬 Start Text Session
            </button>
            <button
              className="protrain-scenario-start-btn protrain-scenario-start-btn--voice"
              onClick={() => onStartSession(selectedScenario.scenarioId, selectedPersona, "voice", selectedVoice)}
            >
              🎤 Start Voice Session
            </button>
          </div>

          <div className="protrain-scenario-voice-select">
            <label>Prospect Voice:</label>
            <div className="protrain-scenario-voice-grid">
              {VOICES.map((v) => (
                <button
                  key={v.id}
                  className={`protrain-scenario-voice-btn ${selectedVoice === v.id ? "protrain-scenario-voice-btn--active" : ""}`}
                  onClick={() => setSelectedVoice(v.id)}
                  title={`${v.label} — ${v.lang}, ${v.gender}`}
                >
                  <span className="protrain-scenario-voice-name">{v.label}</span>
                  <span className="protrain-scenario-voice-meta">{v.lang} · {v.gender}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
